// Third-party
import round from 'lodash/round';

// Utils
import helpers from './helpers';
import { DISCLAIMER_KEYS } from './constants';
import { PortfolioData, getPortfolioHoldingData, getPortfolioYoungestHolding } from '../portfolios';
import { PERIOD_MONTHS } from '../constants';
import { formatLocalizedString, formatMessage } from '../i18n';
import { getLatestPriceForHolding } from '../calculation-utils';
import Dictionary from '@/types/dictionary';
import Portfolio from '@/types/portfolio';
import Disclaimer from '@/types/disclaimer';
import Nullable from '@/types/nullable';
import RollingPerformance from '@/types/rolling-performance';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import Fund from '@/types/fund';
import CommonInception from '@/types/common-inception';

/**
 * Generates the disclaimers to be used inside a comparison for a portfolio.
 *
 * @param portfolio Portfolio used to generate the disclaimers.
 * @param data Funds, stocks and benchmarks data needed to evaluate the portfolio disclaimers.
 */
export const getComparisonPortfolioDisclaimers = (
  portfolio: Portfolio,
  data: PortfolioData,
  validateIncomeStream: boolean,
  shortHistory?: boolean,
  commonInception?: CommonInception,
  keyPrefix: string = 'PORTFOLIO',
) => {
  const disclaimers: Dictionary<Disclaimer> = {};
  const { funds, benchmarks } = data;

  // Find all funds that are missing benchmark data
  const fundsMissingBenchmarks = helpers.getPortfolioHoldingsMissingBenchmarks(portfolio, funds, benchmarks);
  if (fundsMissingBenchmarks.length > 0) {
    const symbols = fundsMissingBenchmarks.map(f => f.symbol || formatLocalizedString(f.name));

    const MISSING_BENCHMARK_DISCLAIMERS = [
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_SHARPE`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_SORTINO`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_UPDOWN`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_BETA`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_TRACKING_ERROR`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_ALPHA`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_INFO_RATIO`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_CORRELATION_COEFFICIENT`],
      DISCLAIMER_KEYS[`${keyPrefix}_NO_BENCHMARK_RSQUARED`],
      DISCLAIMER_KEYS[`${keyPrefix}_MISSING_BENCHMARK`],
    ];

    for (const key of MISSING_BENCHMARK_DISCLAIMERS) {
      disclaimers[key] = {
        key,
        data: {
          list: symbols,
        },
      };
    }
  }

  const hasForcedRollings = ((portfolio.override?.rolling as Nullable<RollingPerformance>)?.monthly || []).length > 0;

  // If portfolio has rollings override, don't bother looking at the historical coverage
  // of the underlying holdings.
  if (!hasForcedRollings) {
    const youngestHolding = getPortfolioYoungestHolding(portfolio, data);
    if (youngestHolding) {
      if (shortHistory) {
        disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_NO_DATA_BEFORE_DATE_SHORT`]] = {
          key: DISCLAIMER_KEYS[`${keyPrefix}_NO_DATA_BEFORE_DATE_SHORT`],
          data: {
            date: youngestHolding.date,
          },
        };
      } else {
        disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_NO_DATA_BEFORE_DATE`]] = {
          key: DISCLAIMER_KEYS[`${keyPrefix}_NO_DATA_BEFORE_DATE`],
          data: {
            date: youngestHolding.date,
            symbol: youngestHolding.symbol || formatLocalizedString(youngestHolding.name),
          },
        };
      }
    }

    const holdingsWithNoData = portfolio.holdings.filter(holding => {
      const holdingData = getPortfolioHoldingData(holding, data);
      return !holdingData || holdingData.performance.rolling.monthly.length < PERIOD_MONTHS['1Y'];
    });

    // Find all holdings with no monthly rolling data.
    if (holdingsWithNoData.length > 0) {
      const list = helpers.formatPortfolioHoldingsList(holdingsWithNoData, data);

      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_NO_DATA`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_NO_DATA`],
        data: {
          list,
        },
      };

      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_NO_RISK_METRICS`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_NO_RISK_METRICS`],
        data: {
          list,
        },
      };
    }

    // Get all holdings missing recent data
    const closedHoldings = portfolio.holdings.filter(h => {
      const holding = getPortfolioHoldingData(h, data);
      return !holding || helpers.holdingIsClosed(holding);
    });

    if (closedHoldings.length > 0) {
      const symbols = closedHoldings.map(h => h.symbol || formatLocalizedString(h.name));

      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_CLOSED_HOLDINGS`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_CLOSED_HOLDINGS`],
        data: {
          list: symbols,
        },
      };
    }

    const closedNavHoldings = helpers.getPortfolioHoldingMissingNavDisclaimers(portfolio, data);

    if (closedNavHoldings.length > 0) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_CLOSED_HOLDINGS_NAV`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_CLOSED_HOLDINGS_NAV`],
        data: {
          list: closedNavHoldings,
        },
      };
    }

    // Get all holdings with less than 1Y data
    const holdingsLessThan1Y = portfolio.holdings.filter(h => {
      const holding = getPortfolioHoldingData(h, data);
      return !holding || helpers.holdingHasLessThan1YData(holding);
    });

    if (holdingsLessThan1Y.length > 0) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_LESS_THAN_1Y`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_LESS_THAN_1Y`],
        data: {
          list: helpers.formatPortfolioHoldingsList(holdingsLessThan1Y, data),
        },
      };
    }

    // Get all holdings with less than 3Y data
    const holdingsLessThan3Y = portfolio.holdings.filter(h => {
      const holding = getPortfolioHoldingData(h, data);
      return !holding || helpers.holdingHasLessThan3YData(holding);
    });

    if (holdingsLessThan3Y.length > 0) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_LESS_THAN_3Y`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_LESS_THAN_3Y`],
        data: {
          list: helpers.formatPortfolioHoldingsList(holdingsLessThan3Y, data),
        },
      };
    }
  }

  if (helpers.portfolioHasMissingOrInvalidHoldings(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_HOLDINGS`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_HOLDINGS`],
    };
  }

  if (!helpers.portfolioHasAllTopHoldingsData(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_TOP_HOLDINGS`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_TOP_HOLDINGS`],
    };
  }

  if (!helpers.portfolioHasAllCountryAllocationData(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_COUNTRY_ALLOC`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_COUNTRY_ALLOC`],
    };
  }

  if (!helpers.portfolioHasAllIndustryHoldingsData(portfolio, data)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_INDUSTRY_HOLDINGS`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_INDUSTRY_HOLDINGS`],
    };
  }

  if (!helpers.portfolioHasAllAssetDetailsData(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_ASSET_DETAILS`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_ASSET_DETAILS`],
    };
  }

  if (!helpers.portfolioHasCreditSecurities(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_CREDIT_QUALITY_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_CREDIT_QUALITY_NOT_APPLICABLE`],
    };

    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MATURITY_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MATURITY_NOT_APPLICABLE`],
    };

    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_METRICS_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_METRICS_NOT_APPLICABLE`],
    };

    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_REGION_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_REGION_NOT_APPLICABLE`],
    };

    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_SECTORS_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_SECTORS_NOT_APPLICABLE`],
    };

    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_STYLE_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_FIXED_INCOME_STYLE_NOT_APPLICABLE`],
    };
  }

  if (helpers.portfolioHasCreditSecurities(portfolio)) {
    if (!helpers.portfolioHasAllMaturityAllocationData(portfolio)) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_MATURITY`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_MATURITY`],
      };
    }

    if (!helpers.portfolioHasAllCreditQualityAllocationData(portfolio)) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_CREDIT_QUALITY`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_CREDIT_QUALITY`],
      };
    }

    if (!helpers.portfolioHasAllFixedIncomeRegionsAllocationData(portfolio)) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_FIXED_INCOME_REGIONS`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_FIXED_INCOME_REGIONS`],
      };
    }

    if (!helpers.portfolioHasAllFixedIncomeSectorsAllocationData(portfolio)) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_FIXED_INCOME_SECTORS`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_FIXED_INCOME_SECTORS`],
      };
    }

    if (!helpers.portfolioHasAllFixedIncomeStyleData(portfolio)) {
      disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_FIXED_INCOME_STYLE`]] = {
        key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_FIXED_INCOME_STYLE`],
      };
    }
  }

  if (!helpers.portfolioHasEquity(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_EQUITY_METRICS_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_EQUITY_METRICS_NOT_APPLICABLE`],
    };

    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_EQUITY_STYLE_NOT_APPLICABLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_EQUITY_STYLE_NOT_APPLICABLE`],
    };
  } else if (!helpers.portfolioHasAllEquityStyleData(portfolio)) {
    disclaimers[DISCLAIMER_KEYS[`${keyPrefix}_MISSING_EQUITY_STYLE`]] = {
      key: DISCLAIMER_KEYS[`${keyPrefix}_MISSING_EQUITY_STYLE`],
    };
  }

  disclaimers[DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA] = {
    key: DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
  };

  const shortDisclaimers = helpers.getPortfolioShortHistoryDisclaimers(portfolio, data, validateIncomeStream, shortHistory, commonInception);

  if (shortDisclaimers.length > 0) {
    disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS] = {
      key: DISCLAIMER_KEYS.SHORT_HOLDINGS,
      data: {
        fields: shortDisclaimers,
      },
    };
  }

  disclaimers[DISCLAIMER_KEYS.NOT_AGGREGATED] = {
    key: DISCLAIMER_KEYS.NOT_AGGREGATED,
  };

  const altDisclaimers = helpers.getPortfolioAltDisclaimers(portfolio, data);
  const customFundDisclaimers = helpers.getPortfolioCustomFundDisclaimers(portfolio, data);

  if (altDisclaimers.length > 0 || customFundDisclaimers.length > 0) {
    disclaimers[DISCLAIMER_KEYS.INSTRUMENT_CUSTOM_DISCLAIMER] = {
      key: DISCLAIMER_KEYS.INSTRUMENT_CUSTOM_DISCLAIMER,
      data: {
        holdings: [...altDisclaimers, ...customFundDisclaimers],
      },
    };
  }

  return disclaimers;
};

/**
 * Generates the disclaimers to be used inside the portfolio card.
 *
 * @param Portfolio Portfolio  used to generate the disclaimers.
 */
export const getPortfolioDisclaimers = (portfolio: Portfolio) => {
  const disclaimers: Dictionary<Disclaimer> = {};
  if (helpers.portfolioHasMissingOrInvalidHoldings(portfolio)) {
    disclaimers[DISCLAIMER_KEYS.PORTFOLIO_MISSING_HOLDINGS] = {
      key: DISCLAIMER_KEYS.PORTFOLIO_MISSING_HOLDINGS,
    };
  }
  return disclaimers;
};

/**
 * Generates the disclaimers to be used inside the portfolio editing dialog.
 *
 * @param portfolioHolding Portfolio holding used to generate the disclaimers.
 * @param data Holding data for the portfolio holding.
 */
export const getPortfolioHoldingDisclaimers = (holding: PortfolioHolding, data?: Holding) => {
  const disclaimers: Dictionary<Disclaimer> = {};
  if (data !== undefined) {
    const firstDataDate = helpers.getFirstDataPointDate(data);
    const lastDataDate = helpers.getLastDataPointDate(data);

    if (helpers.holdingHasNoData(data)) {
      disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_NO_DATA] = {
        key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_NO_DATA,
      };
    }

    if (helpers.holdingIsClosed(data)) {
      disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_CLOSED] = {
        key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_CLOSED,
        data: {
          date: lastDataDate,
        },
      };
    }

    if (helpers.holdingHasLessThan1YData(data)) {
      disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_LESS_THAN_1Y] = {
        key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_LESS_THAN_1Y,
        data: {
          date: firstDataDate,
        },
      };
    }

    if (helpers.holdingHasLessThan3YData(data)) {
      disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_LESS_THAN_3Y] = {
        key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_LESS_THAN_3Y,
        data: {
          date: firstDataDate,
        },
      };
    }

    if (helpers.holdingIsGIC(data)) {
      const holdingAsFund = data as Fund;
      const { customYield } = holdingAsFund;
      if (customYield != null) {
        // Keep up to 2 decimals, but trim any insignificant zeros.
        const term = parseFloat(round(customYield.term, 2).toString());
        const rate = parseFloat(round(customYield.rate, 2).toString());

        disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_GIC] = {
          key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_GIC,
          data: {
            rate,
            date: customYield.startDate,
            term:
              customYield.termUnits === 'months'
                ? `${term} ${formatMessage('EditPortfolioModal.GICSimulator.TermUnits.Months')}`
                : `${term} ${formatMessage('EditPortfolioModal.GICSimulator.TermUnits.Years')}`,
          },
        };
      }
    }

    if (helpers.holdingIsHISA(data)) {
      const holdingAsFund = data as Fund;
      disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_HISA] = {
        key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_HISA,
        data: {
          rate: holdingAsFund.customYield && holdingAsFund.customYield.rate,
        },
      };
    }

    const latestWeeklyPrice = getLatestPriceForHolding(holding.original.type, data);
    disclaimers[DISCLAIMER_KEYS.EDIT_PROTFOLIO_HOLDING_LATEST_WEEKLY_PRICE] = {
      key: DISCLAIMER_KEYS.EDIT_PROTFOLIO_HOLDING_LATEST_WEEKLY_PRICE,
      data: latestWeeklyPrice,
    };
  }

  if (helpers.holdingIsMissing(holding)) {
    disclaimers[DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_MISSING] = {
      key: DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_MISSING,
    };
  }

  return disclaimers;
};
